import axios, { AxiosInstance } from 'axios';
import { useInfiniteQuery, useQuery, UseQueryResult, UseInfiniteQueryResult } from '@tanstack/react-query';
import {
  GetVesselVoyagesParams,
  VoyageDetailResponse,
  VoyageListResponse,
} from '@/types/voyage';

const rawBase =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_PREDICTION_API_URL) ||
  'https://api-prediction.missi-track.com/predict/v1';

// Base URL untuk Prediction API v1 (/predict/v1)
export const VOYAGES_API_BASE_URL = rawBase
  .replace(/\/eta\/?$/, '')
  .replace(/\/routes\/?$/, '')
  .replace(/\/+$/, '');

import errorResponseHandler from '@/api/axios/errorResponseHandler';
import { isSessionExpired } from '@/api/axios/handleUnauthorized';

export const voyagesAxios: AxiosInstance = axios.create({
  baseURL: VOYAGES_API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Interceptor auth token jika ada dan pencegahan request saat 401
voyagesAxios.interceptors.request.use((config: any) => {
  if (isSessionExpired()) {
    const controller = new AbortController();
    controller.abort();
    config.signal = controller.signal;
    return Promise.reject(new axios.CanceledError('Session expired'));
  }
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('_missi_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

voyagesAxios.interceptors.response.use((response) => response, errorResponseHandler);

/**
 * 1. GET /predict/v1/vessels/{vessel_id}/voyages
 * Mengambil daftar riwayat pelayaran untuk satu kapal dengan paginasi
 */
export const getVesselVoyages = async (
  params: GetVesselVoyagesParams,
): Promise<VoyageListResponse> => {
  const {
    vesselId,
    page = 1,
    pageSize = 10,
    status = 'completed',
    includeRoute = true,
    detailedPoints = false,
    startDate,
    endDate,
    originPort,
    destPort,
  } = params;

  const queryParams: Record<string, any> = {
    page,
    page_size: pageSize,
    status,
    include_route: includeRoute,
    detailed_points: detailedPoints,
  };

  if (startDate) queryParams.start_date = startDate;
  if (endDate) queryParams.end_date = endDate;
  if (originPort) queryParams.origin_port = originPort;
  if (destPort) queryParams.dest_port = destPort;

  const response = await voyagesAxios.get<VoyageListResponse>(
    `/vessels/${vesselId}/voyages`,
    { params: queryParams },
  );
  return response.data;
};

/**
 * React Query Infinite Hook untuk Voyage History
 * Mendukung infinite scroll dengan fetchNextPage otomatis
 */
export const useInfiniteQueryVesselVoyages = (
  vesselId: string | null | undefined,
  status: 'completed' | 'ongoing' | 'all' | 'maneuver' | 'stopped' = 'all',
  pageSize: number = 10,
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery({
    queryKey: ['VesselVoyagesInfinite', vesselId, status, pageSize],
    queryFn: ({ pageParam = 1 }) =>
      getVesselVoyages({
        vesselId: vesselId!,
        page: Number(pageParam),
        pageSize,
        status,
        includeRoute: true,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: VoyageListResponse) => {
      if (lastPage && lastPage.page < lastPage.total_pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    staleTime: 1000 * 60 * 3, // 3 menit
    refetchOnWindowFocus: false,
    enabled: !!vesselId && (options?.enabled ?? true),
  });
};

/**
 * 2. GET /predict/v1/voyages/{voyage_key}
 * Mengambil detail 1 pelayaran spesifik beserta jejak rute GPS lengkapnya
 */
export const getVoyageDetail = async (
  voyageKey: string,
): Promise<VoyageDetailResponse> => {
  const response = await voyagesAxios.get<VoyageDetailResponse>(
    `/voyages/${encodeURIComponent(voyageKey)}`,
    {
      params: { detailed_points: true },
    },
  );
  return response.data;
};

export const useQueryVoyageDetail = (
  voyageKey: string | null | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<VoyageDetailResponse, Error> => {
  return useQuery({
    queryKey: ['VoyageDetail', voyageKey],
    queryFn: () => getVoyageDetail(voyageKey!),
    enabled: !!voyageKey && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 10, // 10 menit
  });
};
